// A venue's Setup (spec/13 §13.1, F-HQ-03, ADR-0047): `/v/{venueSlug}/setup`. Its progress at the top ("2 of 3
// essentials · 4 of 9 suggested"), then the three essentials Go live waits on, then the suggested steps, each with a
// line on what it is for, where it stands and one action that opens where it is done — a precise link into the screen
// and row, or the dialog right here (how players pay and the first pass, adding and pairing a PC, inviting staff). Go live is at the bottom, the same
// control as the venue's settings show, off until the essentials are done.
//
// Every step is the server's answer, read from the venue itself, so the page needs no "mark done": it re-reads when
// the window regains focus, and after anything here changes the venue. Reading needs `settings:read`; skipping a
// suggested step and going live need `venues:update` (the server checks both again).

import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Dialog, HeroStat, PageSkeleton, StepTracker, useToast } from '@clavian/ui-web';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { problemCode, PROBLEM } from '../api/problems';
import { queryKeys, useAccess } from '../api/queries';
import { InviteDialog } from '../org/staff/InviteDialog';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { AddStationDialog } from '../stations/AddStationDialog';
import { InstallerDownloads } from '../stations/InstallerDownloads';
import { useStations } from '../stations/queries';
import { CardsSkeleton, Forbidden, Page, Section } from '../ui';
import { readVenue } from '../venue-profile/venue-source';
import { VenueStatus } from '../venue-profile/VenueStatus';
import { HowPlayersPayDialog } from './HowPlayersPayDialog';
import { BootSetupGroup } from './BootSetup';
import { invalidateSetup, useSkipStep, useVenueSetup } from './queries';
import { SetupSteps, StepActionControl } from './SetupSteps';
import { type SetupDialog, venueStepAction } from './steps';

import './setup.css';
import { useProblem } from '../api/use-problem';

const VENUES_UPDATE = 'venues:update';
const STATIONS_MANAGE = 'stations:manage';
const MEMBERS_INVITE = 'members:invite';

export function VenueSetupPage(): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const api = useApi();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const access = useAccess({ venueId: venue.id });
  const canUpdate = access.status === 'ready' && access.can(VENUES_UPDATE);
  const setup = useVenueSetup(venue.organizationId, venue.id);
  const record = useQuery({ queryKey: queryKeys.venue(venue.organizationId, venue.id), queryFn: () => readVenue(api, venue.organizationId, venue.id) });
  const stations = useStations(venue.organizationId, venue.id);
  const skip = useSkipStep(venue.organizationId, venue.id);
  const [dialog, setDialog] = useState<SetupDialog | null>(null);
  const [downloads, setDownloads] = useState(false);

  const title = t('setup.title');
  if (setup.data === undefined) {
    if (setup.isError && problemCode(setup.error) === PROBLEM.forbidden) {
      return (
        <Page title={title}>
          <Forbidden message={t('setup.notPermitted')} />
        </Page>
      );
    }
    return (
      <Page title={title} eyebrow={venue.name}>
        {setup.isError ? <LoadFailure error={setup.error} message={t('setup.unavailable')} onRetry={() => void setup.refetch()} /> : <PageSkeleton label={t('app.loading')} />}
      </Page>
    );
  }

  const data = setup.data;
  const { progress } = data;
  const hasStations = (stations.data?.pages[0]?.data.length ?? 0) > 0;
  const essentials = data.steps.filter((step) => step.kind === 'essential');
  const suggested = data.steps.filter((step) => step.kind === 'suggested');
  const canAddStation = access.status === 'ready' && access.can(STATIONS_MANAGE);
  const canInvite = access.status === 'ready' && access.can(MEMBERS_INVITE);
  const actionFor = (step: (typeof data.steps)[number]) => venueStepAction(step.id, venue.slug, { hasStations, canAddStation, canInvite });
  const closeDialog = () => {
    setDialog(null);
    // A station added or a member invited ticks a step: read setup again.
    void invalidateSetup(queryClient);
  };

  // Guided Clavian Boot setup (F-BOOT-29): not counted in the essentials or the suggested steps' progress.
  const bootOnly = access.status === 'ready' && !access.products.stations;
  const bootGroup = <BootSetupGroup scope={{ organizationId: venue.organizationId, venueId: venue.id }} venueSlug={venue.slug} access={access} bootOnly={bootOnly} />;

  const nextEssential = essentials.find((step) => step.status !== 'done');
  const venueStatus = record.data?.data.status;
  const stationCount = stations.data === undefined ? undefined : (stations.data.pages[0]?.data.length ?? 0);
  const dash = t('setup.hero.unknown');

  return (
    <Page
      title={title}
      eyebrow={venue.name}
      description={t('setup.venueIntro', { venue: venue.name })}
      hero={{
        tileName: venue.name,
        ...(venueStatus === undefined ? {} : { status: <Badge tone={venueStatus === 'live' ? 'ok' : venueStatus === 'paused' ? 'warn' : 'neutral'}>{t(`venueProfile.status.${venueStatus}`)}</Badge> }),
        stats: (
          <>
            <HeroStat label={t('setup.essentialsTitle')} value={progress.essentialsDone} of={t('setup.hero.of', { total: progress.essentialsTotal })} />
            <HeroStat label={t('setup.suggestedTitle')} value={progress.suggestedSettled} of={t('setup.hero.of', { total: progress.suggestedTotal })} />
            <HeroStat label={t('setup.hero.stations')} value={stationCount ?? dash} />
          </>
        ),
      }}
    >
      <StepTracker
        title={t('setup.progressLabel')}
        steps={essentials.map((step) => ({ id: step.id, label: t(`setup.step.${step.id}.title`), done: step.status === 'done' }))}
        next={nextEssential === undefined ? undefined : t('setup.hero.next', { step: t(`setup.step.${nextEssential.id}.title`) })}
        doneText={t('setup.hero.allEssentials')}
        action={
          nextEssential === undefined ? undefined : (
            <StepActionControl action={actionFor(nextEssential)} label={t(`setup.step.${nextEssential.id}.action`)} name={t(`setup.step.${nextEssential.id}.title`)} variant="primary" onDialog={setDialog} />
          )
        }
      />

      {/* A venue with Clavian Boot only starts with it: its PCs come before anything else here (suggested, never essential). */}
      {bootOnly ? bootGroup : null}

      <Section title={t('setup.essentialsTitle')} tip={t('setup.essentialsHint')} body="rows">
        <SetupSteps
          steps={essentials}
          label={t('setup.essentialsTitle')}
          actionFor={actionFor}
          onDialog={setDialog}
        />
        {essentials.some((step) => step.id === 'stationPaired' && step.status !== 'done') ? (
          <div className="console-setup-aside">
            <Button variant="ghost" size="sm" icon={<Icon name="download" />} onClick={() => setDownloads(true)}>
              {t('setup.downloadApps')}
            </Button>
          </div>
        ) : null}
      </Section>

      <Section title={t('setup.suggestedTitle')} tip={t('setup.suggestedHint')} body="rows">
        <SetupSteps
          steps={suggested}
          label={t('setup.suggestedTitle')}
          actionFor={actionFor}
          onDialog={setDialog}
          busyStep={skip.isPending ? skip.variables?.step : undefined}
          onSkip={
            canUpdate
              ? (step, skipIt) =>
                  skip.mutate(
                    { step: step.id as Parameters<typeof skip.mutate>[0]['step'], skip: skipIt },
                    { onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('setup.skipFailed') }) }) },
                  )
              : undefined
          }
        />
      </Section>

      {bootOnly ? null : bootGroup}

      <Section title={t('setup.goLive.title')}>
        {record.data === undefined ? (
          record.isError ? (
            <LoadFailure error={record.error} onRetry={() => void record.refetch()} />
          ) : (
            <CardsSkeleton count={1} height="4rem" label={t('app.loading')} />
          )
        ) : (
          <VenueStatus
            organizationId={venue.organizationId}
            venueId={venue.id}
            venue={record.data}
            canEdit={canUpdate}
            onVersion={(next) => queryClient.setQueryData(queryKeys.venue(venue.organizationId, venue.id), next)}
          />
        )}
      </Section>

      {canAddStation ? (
        <AddStationDialog organizationId={venue.organizationId} venueId={venue.id} open={dialog === 'addStation'} onOpenChange={(open) => (open ? undefined : closeDialog())} />
      ) : null}
      {canInvite ? (
        <InviteDialog open={dialog === 'inviteStaff'} onOpenChange={(open) => (open ? undefined : closeDialog())} />
      ) : null}
      <HowPlayersPayDialog open={dialog === 'howPlayersPay'} onOpenChange={(open) => (open ? undefined : closeDialog())} />
      <Dialog open={downloads} onOpenChange={setDownloads} size="md" title={t('stations.installers.sheetTitle')} description={t('stations.installers.sheetHint')}>
        {downloads ? <InstallerDownloads organizationId={venue.organizationId} venueId={venue.id} /> : null}
      </Dialog>
    </Page>
  );
}
